import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { toast } from 'sonner';

interface Props {
  disciplinaId: string;
  setDisciplinaId: (v: string) => void;
  conteudoId: string;
  setConteudoId: (v: string) => void;
  tipoEstudo: string;
  setTipoEstudo: (v: string) => void;
  tempo: string;
  setTempo: (v: string) => void;
  data: string;
  setData: (v: string) => void;
  paginaInicio: string;
  setPaginaInicio: (v: string) => void;
  paginaFim: string;
  setPaginaFim: (v: string) => void;
  showAddTipo: boolean;
  setShowAddTipo: (v: boolean) => void;
  novoTipoEstudo: string;
  setNovoTipoEstudo: (v: string) => void;
}

export function FieldsBasicos({
  disciplinaId, setDisciplinaId,
  conteudoId, setConteudoId,
  tipoEstudo, setTipoEstudo,
  tempo, setTempo,
  data, setData,
  paginaInicio, setPaginaInicio,
  paginaFim, setPaginaFim,
  showAddTipo, setShowAddTipo,
  novoTipoEstudo, setNovoTipoEstudo,
}: Props) {
  const { disciplinas, getConteudosByDisciplina, tiposEstudo, addTipoEstudo } = useApp();
  const { bloqueadas } = usePlanLimits();
  const disciplinasLiberadas = disciplinas.filter(d => !bloqueadas.has(d.id));
  const conteudos = disciplinaId ? getConteudosByDisciplina(disciplinaId) : [];

  const handleAddTipoEstudo = async () => {
    if (!novoTipoEstudo.trim()) {
      toast.error('Digite um nome para o tipo de estudo');
      return;
    }
    await addTipoEstudo(novoTipoEstudo.trim());
    toast.success('Tipo de estudo adicionado!');
    setNovoTipoEstudo('');
    setShowAddTipo(false);
  };

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Disciplina *</Label>
          <Select value={disciplinaId} onValueChange={setDisciplinaId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a disciplina" />
            </SelectTrigger>
            <SelectContent>
              {disciplinasLiberadas.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Conteúdo *</Label>
          <Select value={conteudoId} onValueChange={setConteudoId} disabled={!disciplinaId}>
            <SelectTrigger>
              <SelectValue placeholder={disciplinaId ? "Selecione o conteúdo" : "Selecione uma disciplina primeiro"} />
            </SelectTrigger>
            <SelectContent>
              {conteudos.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Tipo de Estudo</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAddTipo(!showAddTipo)}
              className="h-7 px-2"
            >
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
          </div>
          {showAddTipo && (
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Nome do tipo..."
                value={novoTipoEstudo}
                onChange={(e) => setNovoTipoEstudo(e.target.value)}
                className="flex-1"
              />
              <Button type="button" size="sm" onClick={handleAddTipoEstudo}>
                Salvar
              </Button>
            </div>
          )}
          <Select value={tipoEstudo} onValueChange={setTipoEstudo}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo de estudo" />
            </SelectTrigger>
            <SelectContent>
              {tiposEstudo.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Tempo (min) *</Label>
          <Input
            type="number"
            min={1}
            value={tempo}
            onChange={(e) => setTempo(e.target.value)}
            placeholder="25"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Páginas Lidas</Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              min={1}
              value={paginaInicio}
              onChange={(e) => setPaginaInicio(e.target.value)}
              placeholder="Início"
            />
            <Input
              type="number"
              min={1}
              value={paginaFim}
              onChange={(e) => setPaginaFim(e.target.value)}
              placeholder="Término"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Data *</Label>
          <Input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
        </div>
      </div>
    </>
  );
}
